import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Ban, Boxes, Clock, Gauge, History, KeySquare, Loader2, Package, Pencil, Trash2, Users, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { OperacjeModal } from '@/components/OperacjeModal';
import { ZmianaNumeruKarty } from '@/components/ZmianaNumeruKarty';
import { HistoriaKarty } from '@/components/HistoriaKarty';
import { UsuwanieKart } from '@/components/UsuwanieKart';
import { Badge } from '@/components/ui/badge';
import { WyjatekHrPrzelacznik, ZnacznikHr } from '@/components/ZnacznikHr';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { czasSps, dataSps } from '@/lib/czas-sps';
import { fetchEmployeeCard, type EmployeeCard as Karta, type LimitNaKarcie } from '@/lib/employee-card-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { kodZnany, nazwaKodu, nazwaOperacji } from '@/lib/kody-operacji';
import { iloscNaEkran } from '@/lib/znak-ilosci';
import { EmployeeModal } from '@/pages/Employees';

const nf = new Intl.NumberFormat('pl-PL');
const nfPln = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });

/**
 * KARTA PRACOWNIKA — jeden ekran zamiast pięciu.
 *
 * ⚠️ TO JEST WIDOK, NIE FORMULARZ. Zapis zostaje tam, gdzie był: okno edycji, zmiana numeru,
 * operacje systemowe, Uprawnienia. Drugi zestaw pól zapisujących te same kolumny znaczyłby drugie
 * miejsce, w którym trzeba pamiętać o `ALLOWLOGIN`, o tym, że PIN-u nie da się odczytać, i o tym,
 * że pusta lista powiązań znaczy „odbierz wszystkie".
 *
 * ⚠️ EKRAN MA ODPOWIADAĆ NA JEDNO PYTANIE: „czemu ten człowiek nie może pobrać rękawic". Dlatego
 * rzeczy, które NIE POZWALAJĄ pobrać, idą na górę i mają własne pasy ostrzegawcze — konto
 * zablokowane, wyłączone egzekwowanie limitów, brak dostępu do automatu. Ładny układ, w którym
 * te trzy fakty trzeba wyłuskać wzrokiem z listy pól, byłby na tym ekranie bezużyteczny.
 */

/** Kafelek liczbowy nad treścią. */
function Kafelek({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" title={hint}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Sekcja({
  tytul,
  ikona: Ikona,
  licznik,
  children,
  akcja,
}: {
  tytul: string;
  ikona?: typeof Users;
  licznik?: string;
  children: React.ReactNode;
  akcja?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {Ikona && <Ikona className="size-4 text-muted-foreground" />}
          {tytul}
          {licznik !== undefined && <span className="text-xs font-normal text-muted-foreground">{licznik}</span>}
        </h2>
        {akcja}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Wiersz „etykieta → wartość" w danych pracownika. */
function Pole({ label, children, kolumna }: { label: string; children: React.ReactNode; kolumna?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1.5 last:border-b-0">
      <span className="shrink-0 text-xs text-muted-foreground">
        {label}
        {kolumna && <span className="ml-1 font-mono text-[10px] opacity-60">{kolumna}</span>}
      </span>
      <span className="min-w-0 text-right text-sm">{children}</span>
    </div>
  );
}

/**
 * PASEK WYKORZYSTANIA LIMITU — kolory ODWROTNE niż w pasku zapełnienia.
 *
 * ⚠️ NIE UŻYWAMY `PasekZapelnienia`. Tam pełno znaczy dobrze (towar w automacie), tutaj pełno
 * znaczy „nic już nie pobierze". Wspólny komponent musiałby przyjąć kierunek jako parametr,
 * a wtedy jedno wywołanie bez parametru maluje limit na zielono dokładnie wtedy, gdy jest
 * wyczerpany.
 */
function PasekLimitu({ pobrano, maxQty }: { pobrano: number; maxQty: number }) {
  const procent = maxQty > 0 ? (pobrano / maxQty) * 100 : 0;
  const kolor = procent >= 100 ? 'var(--destructive)' : procent >= 80 ? 'var(--warning)' : 'var(--success)';
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <span
        className="block h-full rounded-full"
        style={{ width: `${Math.min(100, Math.max(0, procent))}%`, background: kolor }}
      />
    </span>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *  WIERSZ LIMITU — TYLKO DO ODCZYTU, Z DROGĄ DO EDYCJI.
 *
 *  ⚠️⚠️ TU NIE MA ZAPISU I TO JEST CELOWE — zapis limitów mieszka na ekranie LIMITÓW (Uprawnienia).
 *  Przez chwilę było inaczej: 07.09.2026 wstawiłem tutaj edycję limitów imiennych, bo odczytałem
 *  rozstrzygnięcie „musi być na karcie limity" jako kartę PRACOWNIKA. Chodziło o ekran Limitów,
 *  gdzie sekcje OSÓB już są — więc edycja wróciła tam, razem z regułami, które nie mają wpisu
 *  dostępu (też pokazywanymi tam jako wiersze).
 *
 *  ⚠️ DLACZEGO TO WAŻNE, A NIE TYLKO PORZąDKOWE: dwa miejsca zapisu tej samej reguły znaczyłyby
 *  dwie drogi do SPS i dwa zestawy ostrzeżeń (o jednostce okna, o zasięgu nadgrupy, o `RATIONEFFECT`).
 *  Karta ma prowadzić do miejsca zapisu, nie być jego kopią.
 *
 *  ⚠️ WIERSZ MÓWI, GDZIE SZUKAĆ, i różnicuje po źródle: imienny należy do tej osoby, grupowy do
 *  grupy — a to zmienia, kogo dotknie zmiana.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */
function KartaLimitu({ r }: { r: LimitNaKarcie }) {
  const { t } = useI18n();
  const przekroczony = r.pobrano > r.maxQty;
  const wyczerpany = !przekroczony && r.zostalo === 0;

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{r.zakres.name}</p>
          <p className="text-xs text-muted-foreground">
            {r.zakres.kind === 'itemGroup' ? t('profile.scopeGroup') : t('profile.scopeItem')}
            {' · '}
            {r.zrodlo.kind === 'group'
              ? t('profile.fromGroup').replace('{name}', r.zrodlo.name)
              : t('profile.fromPerson')}
          </p>
        </div>
        {przekroczony ? (
          <Badge variant="destructive">{t('profile.limitOver')}</Badge>
        ) : wyczerpany ? (
          <Badge variant="warning">{t('profile.limitEmpty')}</Badge>
        ) : (
          <Badge variant="success">
            {t('profile.limitLeft').replace('{n}', String(r.zostalo))}
          </Badge>
        )}
      </div>

      <div className="mt-2 space-y-1">
        <PasekLimitu pobrano={r.pobrano} maxQty={r.maxQty} />
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground tabular-nums">
            {r.pobrano} / {r.maxQty}
          </span>{' '}
          {t('profile.limitPerWindow').replace('{okno}', r.oknoLabel)}
          {r.wydan > 0 && <> · {t('profile.limitFromIssues').replace('{n}', String(r.wydan))}</>}
        </p>
        {/* ⚠️ Reguła miesięczna/roczna ISTNIEJE, ale SPSUI jej nie pilnuje — pokazanie jej
            bez tej uwagi byłoby obietnicą ograniczenia, którego przy automacie nie ma. */}
        {r.nieegzekwowany && (
          <p className="flex items-start gap-1 text-xs text-[var(--warning)]">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            {t('profile.limitUnenforced')}
          </p>
        )}

        {/*
          ⚠️⚠️ DROGA DO EDYCJI, NIE PRZYCISK ZAPISU — patrz nagłówek. Zdanie różnicuje po źródle,
          bo to ono decyduje, KOGO dotknie zmiana: limit imienny tę osobę, grupowy całą grupę.
          Milczenie czytałoby się jako „tego nie da się ruszyć", a to nieprawda.
        */}
        <p className="pt-1 text-xs text-muted-foreground">
          {r.zrodlo.kind === 'employee' ? t('profile.limitEditPersonal') : t('profile.limitFromGroupEdit')}
        </p>
      </div>
    </li>
  );
}

export function EmployeeCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { groupId, island } = useIslands();
  const { isAdmin } = useAuth();
  const [operacjeOtwarte, setOperacjeOtwarte] = useState(false);
  /** ⚠️ HOOK NAD KAŻDYM `return` — ten ekran ma wczesne wyjścia (wczytywanie, brak wyspy). */
  const [historiaKarty, setHistoriaKarty] = useState(false);
  const [numerOtwarty, setNumerOtwarty] = useState(false);
  const [edycjaOtwarta, setEdycjaOtwarta] = useState(false);
  const [usuwanieOtwarte, setUsuwanieOtwarte] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ['employees', 'card', id, groupId],
    queryFn: () => fetchEmployeeCard(id!, groupId!),
    enabled: Boolean(id) && Boolean(groupId),
  });



  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  if (isPending) {
    return (
      <p className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading')}
      </p>
    );
  }

  if (error || !data) {
    /**
     * ⚠️ ODMOWA MÓWI, CO SIĘ STAŁO, i daje drogę powrotną. Najczęstszy powód to przełączenie wyspy
     * przy otwartej karcie — pracownik należy do poprzedniej, więc API słusznie go nie zna.
     */
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-sm font-medium">{t('profile.notFound')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('profile.notFoundHint')}</p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to="/pracownicy">{t('profile.back')}</Link>
        </Button>
      </div>
    );
  }

  const k: Karta = data;
  const p = k.pracownik;
  const dostepne = k.automaty.filter((m) => m.bezposrednio || m.zGrup.length > 0);
  const operacjeCzynne = k.operacje.kody.filter((o) => o.wlasny || o.zGrup.length > 0);
  const zablokowane = k.limity.reguly.filter((r) => r.zostalo === 0).length;

  return (
    <div className="space-y-4">
      {/* ── NAGŁÓWEK ─────────────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            to="/pracownicy"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            {t('profile.back')}
          </Link>
          {/* Wyspa w nagłówku, jak okruszki nad tabelami — karta pokazuje dane JEDNEJ wyspy
              i musi to mówić, zanim ktoś porówna liczby z innej. */}
          {island && (
            <span className="text-xs text-muted-foreground">
              {island.clientName} · {island.name}
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid size-12 shrink-0 place-items-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
              {inicjaly(p.name)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold">
                  {p.name.trim() === '' ? <span className="italic text-muted-foreground">{t('emp.bezNazwy')}</span> : p.name}
                </h1>
                {p.active ? (
                  <Badge variant="success">{t('emp.statusActive')}</Badge>
                ) : (
                  <Badge variant="neutral">{t('emp.statusBlocked')}</Badge>
                )}
                {p.pending && (
                  <Badge variant="warning" title={t('emp.pendingHint')}>
                    <Clock className="size-3" />
                    {t('profile.pending')}
                  </Badge>
                )}
                {p.usunietaZAutomatow && (
                  <Badge variant="neutral">
                    <UserX className="size-3" />
                    {t('emp.usunieta')}
                  </Badge>
                )}
                <ZnacznikHr stan={p.hrPlus} numer={p.hrPlusNumer} />
                {isAdmin && p.hrPlus && p.hrPlusPowiazana === false && (
                  <WyjatekHrPrzelacznik groupId={groupId} employeeId={p.id} wyjatek={p.hrPlusWyjatek === true} />
                )}
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                nr <span className="font-mono">{p.spsUserNumber}</span>
                {p.alias && <> · {p.alias}</>}
                {p.costCenter && (
                  <>
                    {' · '}
                    <span className="text-[var(--primary)]">CK {p.costCenter}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* ⚠️ OSOBA USUNIĘTA Z AUTOMATÓW: w SPS nie ma jej wiersza, więc edycja, operacje i zmiana numeru
              poszłyby w próżnię. Zostaje trwałe usunięcie z Vendi; powrót karty = dodanie osoby z tym numerem. */}
          {p.usunietaZAutomatow ? (
            <div className="flex flex-wrap gap-2">
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={() => setUsuwanieOtwarte(true)}>
                  <Trash2 />
                  {t('emp.usunTrwale')}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setOperacjeOtwarte(true)}>
                <KeySquare />
                {t('ops.action')}
              </Button>
              {isAdmin && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setUsuwanieOtwarte(true)}>
                    <Trash2 />
                    {t('emp.usunZAutomatow')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setNumerOtwarty(true)}>
                    {t('emp.cardChangeAction')}
                  </Button>
                  {/* ⚠️ TO JEST TEN SAM `EmployeeModal` CO NA LIŚCIE, a nie drugi formularz. Jeden
                      formularz pracownika = jedno miejsce, które zna pułapki zapisu (patrz komentarz
                      na górze pliku i przy samym oknie). */}
                  <Button size="sm" onClick={() => setEdycjaOtwarta(true)}>
                    <Pencil />
                    {t('common.edit')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {p.usunietaZAutomatow && (
        <Pas ton="warning" ikona={UserX}>
          {t(p.usunietaZAutomatowWezel ? 'emp.usunietaHintWezel' : 'emp.usunietaHint')
            .replace('{kiedy}', new Date(p.usunietaZAutomatow).toLocaleString())
            .replace('{wezel}', p.usunietaZAutomatowWezel ?? '')}
        </Pas>
      )}

      {/* ── CO BLOKUJE POBRANIE ──────────────────────────────────────────────────── */}
      {!p.active && (
        <Pas ton="destructive" ikona={Ban}>
          {t('profile.blockedWarn')}
        </Pas>
      )}
      {/* ⚠️ RATIONEFFECT ≠ 3 ZNACZY, ŻE ŻADEN LIMIT NIŻEJ NIE OBOWIĄZUJE. Bez tego pasa lista
          reguł z paskami i procentami mówiłaby coś dokładnie odwrotnego do prawdy. */}
      {p.rationEffect !== 3 && (
        <Pas ton="warning" ikona={Gauge}>
          {t('profile.rationOffWarn').replace('{n}', String(p.rationEffect))}
        </Pas>
      )}
      {dostepne.length === 0 && (
        <Pas ton="warning" ikona={Boxes}>
          {t('profile.noMachineWarn')}
        </Pas>
      )}
      {/**
        * ⚠️ ZAKRES DAT POKAZUJEMY WYŁĄCZNIE PRZY WŁĄCZONEJ FLADZE. Same daty nic nie znaczą:
        * na wyspie testowej `DATERANGESTART`/`END` są wypełnione u WSZYSTKICH 762 osób, a flaga
        * jest wyłączona u wszystkich. Pierwsza wersja tego pasa czytała same daty i ogłaszała
        * każdemu pracownikowi zakres ważności, którego automat nie egzekwuje — czyli pokazywała
        * nieprawdę, i to na ekranie, którego jedynym zadaniem jest powiedzieć, co blokuje pobranie.
        */}
      {p.dateRangeEnable && (
        <Pas ton="neutral" ikona={Clock}>
          {t('profile.dateRange')
            .replace('{od}', p.dateRangeStart ?? '—')
            .replace('{do}', p.dateRangeEnd ?? '—')}
        </Pas>
      )}

      {/* ── KAFELKI ──────────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Kafelek
          label={t('profile.kpi.issues').replace('{d}', String(k.aktywnosc.oknoDni))}
          value={nf.format(k.aktywnosc.wydan)}
        />
        <Kafelek label={t('profile.kpi.units')} value={nf.format(k.aktywnosc.sztuk)} />
        <Kafelek label={t('profile.kpi.value')} value={nfPln.format(Number(k.aktywnosc.wartosc))} />
        <Kafelek
          label={t('profile.kpi.last')}
          value={k.aktywnosc.ostatnie ? dataSps(k.aktywnosc.ostatnie) : '—'}
          hint={k.aktywnosc.ostatnie ? czasSps(k.aktywnosc.ostatnie) : undefined}
        />
        <Kafelek label={t('profile.kpi.machines')} value={`${dostepne.length} / ${k.automaty.length}`} />
        <Kafelek
          label={t('profile.kpi.limits')}
          value={zablokowane > 0 ? `${k.limity.reguly.length} (${zablokowane}!)` : String(k.limity.reguly.length)}
          hint={zablokowane > 0 ? t('profile.kpi.limitsHint') : undefined}
        />
      </div>

      {k.wydaniaBezPrzypisania > 0 && (
        <Pas ton="neutral" ikona={AlertTriangle}>
          {t('profile.orphanIssues').replace('{n}', String(k.wydaniaBezPrzypisania))}
        </Pas>
      )}

      {/*
        DANE PRACOWNIKA PO LEWEJ, jego historia i uprawnienia po prawej — zgłoszone 01.09.2026
        („zamienić miejscami wszystkie dane w kafelkach z prawej na lewą stronę").

        ⚠️ KOLEJNOŚĆ W KODZIE ZOSTAJE (treść, potem dane), a stronami zamienia `order` przy `xl`.
        Poniżej `xl` siatka jest jednokolumnowa i kolejność w kodzie jest kolejnością CZYTANIA:
        na telefonie limity i wydania mają stać przed numerem karty, którą i tak widać w nagłówku.
      */}
      <div className="grid gap-4 xl:grid-cols-[22rem_1fr]">
        <div className="min-w-0 space-y-4 xl:order-2">
          {/* ── LIMITY ───────────────────────────────────────────────────────────── */}
          <Sekcja tytul={t('profile.limits')} ikona={Gauge} licznik={`(${k.limity.reguly.length})`}>
            {k.limity.reguly.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('profile.limitsNone')}</p>
            ) : (
              <ul className="space-y-2">
                {k.limity.reguly.map((r) => (
                  <KartaLimitu key={r.id} r={r} />
                ))}
              </ul>
            )}
            {/* ⚠️ ZOSTAJE TYLKO WARUNKOWE ZDANIE o regułach poza zasięgiem — ono mówi o TEJ osobie.
                Wykład o liczeniu per osoba poszedł 01.09.2026 razem z resztą opisówek. */}
            {k.limity.pozaZasiegiem > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                {t('profile.limitsOutOfScope').replace('{n}', String(k.limity.pozaZasiegiem))}
              </p>
            )}
          </Sekcja>

          {/* ── DOSTĘP DO PRODUKTÓW ──────────────────────────────────────────────── */}
          <Sekcja tytul={t('profile.access')} ikona={Package} licznik={`(${k.uprawnienia.wpisy.length})`}>
            {k.uprawnienia.wpisy.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('profile.accessNone')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {k.uprawnienia.wpisy.map((u) => (
                  <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0">
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{u.zakres.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {u.zakres.kind === 'itemGroup'
                          ? t('profile.scopeGroup')
                          : u.zakres.kind === 'item'
                            ? t('profile.scopeItem')
                            : u.zakres.kind === 'itemSubGroup'
                              ? t('profile.scopeSubGroup')
                              : t('profile.scopeAll')}
                        {' · '}
                        {u.zrodlo.kind === 'group'
                          ? t('profile.fromGroup').replace('{name}', u.zrodlo.name)
                          : u.zrodlo.kind === 'all'
                            ? t('profile.fromAll')
                            : t('profile.fromPerson')}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {/* ⚠️ Wiersz zawężony przez zadanie/automat obowiązuje WĘŻEJ, niż widać —
                          plakietka jest jedynym miejscem, gdzie to widać na tym ekranie. */}
                      {u.zawezone.length > 0 && (
                        <Badge variant="neutral" title={t('profile.narrowedHint').replace('{cols}', u.zawezone.join(', '))}>
                          {t('profile.narrowed')}
                        </Badge>
                      )}
                      {u.maLimit ? (
                        <Badge variant="warning">{t('profile.withLimit')}</Badge>
                      ) : (
                        <Badge variant="neutral">{t('profile.noLimit')}</Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {k.uprawnienia.nierozpoznane.length > 0 && (
              <p className="mt-3 text-xs text-[var(--warning)]">
                {t('profile.accessUnknown').replace('{n}', String(k.uprawnienia.nierozpoznane.length))}
              </p>
            )}
          </Sekcja>

          {/* ── OSTATNIE WYDANIA ─────────────────────────────────────────────────── */}
          <Sekcja
            tytul={t('profile.history')}
            ikona={Clock}
            /* ⚠️ „ostatnie N", a nie samo „(N)": goła liczba czytałaby się jak CAŁA historia
               tej osoby, a to jest wycinek — reszta jest w Transakcjach. */
            licznik={t('profile.historyLast').replace('{n}', String(k.ostatnieWydania.length))}
            akcja={
              <Button asChild variant="outline" size="sm" className="h-7 text-xs">
                <Link to="/transakcje">{t('profile.allTransactions')}</Link>
              </Button>
            }
          >
            {k.ostatnieWydania.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('profile.historyNone')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-1.5 pr-3 font-medium">{t('tx.when')}</th>
                      <th className="py-1.5 pr-3 font-medium">{t('tx.machine')}</th>
                      <th className="py-1.5 pr-3 font-medium">{t('tx.code')}</th>
                      <th className="py-1.5 pr-3 font-medium">{t('tx.item')}</th>
                      <th className="py-1.5 pr-3 text-right font-medium">{t('tx.qty')}</th>
                      <th className="py-1.5 text-right font-medium">{t('tx.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {k.ostatnieWydania.map((w) => {
                      const znak = iloscNaEkran(w.transCode, w.qty);
                      return (
                        <tr key={w.id} className="border-b border-border/60 last:border-b-0">
                          <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">{czasSps(w.ts)}</td>
                          <td className="whitespace-nowrap py-1.5 pr-3 text-muted-foreground">
                            {w.machine.vmid} — {w.machine.name}
                          </td>
                          <td className="py-1.5 pr-3">
                            <span title={kodZnany(w.transCode) ? undefined : t('ops.unnamedHint')}>
                              {nazwaKodu(t, w.transCode)}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3">{w.item?.name ?? '—'}</td>
                          {/* ⚠️ ZNAK LICZONY Z KODU OPERACJI. `QTY` w SPS jest dodatnie i dla wydania,
                              i dla uzupełnienia — kierunek niesie `TRANSCODE`, nie liczba. */}
                          <td
                            className={`py-1.5 pr-3 text-right tabular-nums ${
                              znak.startsWith('-') ? 'text-[var(--destructive)]' : 'text-[var(--success)]'
                            }`}
                          >
                            {znak}
                          </td>
                          <td className="whitespace-nowrap py-1.5 text-right tabular-nums text-muted-foreground">
                            {nfPln.format(Math.abs(w.qty) * Number(w.unitCost))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Sekcja>
        </div>

        {/* ── KOLUMNA DANYCH (po lewej od `xl`) ────────────────────────────────────── */}
        <aside className="space-y-4 xl:order-1">
          <Sekcja tytul={t('profile.data')}>
            <div className="space-y-0">
              <Pole label={t('emp.cardNumber')} kolumna="USERNUMBER">
                <span className="flex items-center gap-1.5">
                  <span className="font-mono">{p.spsUserNumber}</span>
                  {/*
                    ⚠️⚠️ ZEGAREK PRZY NUMERZE, TAK JAK PRZY LIMICIE (zamówione 03.09.2026) — i to
                    jest pierwsza rzecz, której trzeba przy pytaniu „gdzie są jego stare wydania".
                    Kaskada SPS-a przy zmianie numeru NIE RUSZA `TransactionLog` (zmierzone
                    u klienta: 138 wydań bez właściciela), więc starszych wydań szuka się pod
                    numerem POPRZEDNIM — a tego nie da się skądkolwiek odczytać bez tej listy.
                  */}
                  <button
                    type="button"
                    title={t('histKarty.pokaz')}
                    aria-label={t('histKarty.pokaz')}
                    onClick={() => setHistoriaKarty(true)}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <History className="size-3.5" aria-hidden />
                  </button>
                </span>
              </Pole>
              <Pole label={t('emp.alias')} kolumna="ALIAS">
                {p.alias ?? '—'}
              </Pole>
              <Pole label={t('emp.costCenter')} kolumna="COSTCENTER">
                {p.costCenter ?? '—'}
              </Pole>
              {/*
                ⚠️ BĄBELEK, NIE GOŁY NAPIS (zgłoszone 01.09.2026). „Konto aktywne: wyłączony" to
                jedyna rzecz w tej karcie, która sama z siebie tłumaczy, dlaczego ktoś nie pobierze
                — a w szarym wierszu wyglądała jak każde inne ustawienie i dawała się przeczytać
                odwrotnie. Kolor niesie tu tę samą informację co słowo.
              */}
              <Pole label={t('emp.active')} kolumna="ALLOWLOGIN">
                <Badge variant={p.active ? 'success' : 'destructive'}>
                  {p.active ? t('common.enabled') : t('common.disabled')}
                </Badge>
              </Pole>
              <Pole label={t('emp.rationEffect')} kolumna="RATIONEFFECT">
                {t(`emp.rationEffect${p.rationEffect}`)}
              </Pole>
            </div>

            {/* ⚠️ POKAZUJEMY TYLKO WŁĄCZONE znaczniki. Szesnaście wierszy „nie" spycha te dwa
                istotne poza ekran, a ustawienie wyłączone jest stanem domyślnym. */}
            <div className="mt-3 border-t border-border pt-3">
              <p className="text-xs text-muted-foreground">{t('profile.flagsOn')}</p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {p.znaczniki.filter((z) => z.wlaczony).length === 0 ? (
                  <span className="text-xs text-muted-foreground">{t('profile.flagsNone')}</span>
                ) : (
                  p.znaczniki
                    .filter((z) => z.wlaczony)
                    .map((z) => (
                      <Badge key={z.pole} variant="neutral" title={z.kolumna}>
                        {t(`emp.flag.${z.pole}`)}
                      </Badge>
                    ))
                )}
              </div>
            </div>
          </Sekcja>

          <Sekcja tytul={t('profile.groups')} ikona={Users} licznik={`(${k.grupy.length})`}>
            {k.grupy.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('emp.noGroup')}</p>
            ) : (
              <ul className="space-y-1.5">
                {k.grupy.map((g) => (
                  <li key={g.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate">{g.name}</span>
                      <span className="block truncate font-mono text-[10px] text-muted-foreground">
                        {g.spsGroupNumber}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t('profile.groupMembers').replace('{n}', String(g.osob))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/*
              ⚠️ CENTRUM KOSZTOWE STOI TYLKO W „DANE PRACOWNIKA" (zgłoszone 01.09.2026: „centrum
              kosztowe jest zdublowane — zostaw tylko to w Dane pracownika"). Tu powtarzało tę samą
              wartość i dopisywało do niej drugą postać z SPS.

              ⚠️ ROZJAZD DWÓCH POSTACI NIE ZNIKNĄŁ Z PORTALU: liczy go ekran „Centra kosztowe"
              (kolumna „Rozjazd" i lista „Pokaż ich"), czyli miejsce, w którym się to poprawia
              hurtem, a nie karta jednej osoby.
            */}
          </Sekcja>

          <Sekcja
            tytul={t('profile.machines')}
            ikona={Boxes}
            licznik={`(${dostepne.length}/${k.automaty.length})`}
          >
            {k.automaty.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('emp.machinesEmpty')}</p>
            ) : (
              <ul className="space-y-1.5">
                {k.automaty.map((m) => {
                  const ma = m.bezposrednio || m.zGrup.length > 0;
                  return (
                    <li key={m.id} className={`flex items-center justify-between gap-2 text-sm ${ma ? '' : 'opacity-45'}`}>
                      <span className="min-w-0 truncate">
                        <span className="font-mono text-xs text-muted-foreground">VMID {m.vmid}</span> {m.name}
                      </span>
                      <span className="flex shrink-0 gap-1">
                        {m.bezposrednio && <Badge variant="default">{t('profile.direct')}</Badge>}
                        {m.zGrup.length > 0 && (
                          <Badge variant="neutral" title={m.zGrup.join(', ')}>
                            {t('profile.viaGroup')}
                          </Badge>
                        )}
                        {!ma && <Badge variant="neutral">{t('profile.noAccess')}</Badge>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Sekcja>

          <Sekcja
            tytul={t('ops.title')}
            ikona={KeySquare}
            licznik={`(${operacjeCzynne.length})`}
            akcja={
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOperacjeOtwarte(true)}>
                {t('common.edit')}
              </Button>
            }
          >
            {operacjeCzynne.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('profile.opsNone')}</p>
            ) : (
              <ul className="space-y-1.5">
                {operacjeCzynne.map((o) => (
                  <li key={o.code} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate" title={kodZnany(o.code) ? undefined : t('ops.unnamedHint')}>
                      {/* Nazwa prowadzi, kod w nawiasie — uzasadnienie przy tej samej zmianie w `OperacjeModal`. */}
                      <span>{nazwaOperacji(t, o.code)}</span>
                      {kodZnany(o.code) && (
                        <span className="ml-1.5 font-mono text-xs text-muted-foreground">{o.code}</span>
                      )}
                    </span>
                    <span className="flex shrink-0 gap-1">
                      {o.wlasny && <Badge variant="default">{t('profile.direct')}</Badge>}
                      {o.zGrup.length > 0 && (
                        <Badge variant="neutral" title={o.zGrup.join(', ')}>
                          {t('profile.viaGroup')}
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Sekcja>
        </aside>
      </div>

      {edycjaOtwarta && (
        <EmployeeModal
          open
          onOpenChange={setEdycjaOtwarta}
          editing={{ id: p.id, spsUserNumber: p.spsUserNumber }}
          groupId={groupId}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
          /** Zmiana numeru zamyka edycję i otwiera swoje okno — jak na liście. */
          onZmienNumer={() => {
            setEdycjaOtwarta(false);
            setNumerOtwarty(true);
          }}
        />
      )}

      {usuwanieOtwarte && groupId && (
        <UsuwanieKart
          groupId={groupId}
          numery={[p.spsUserNumber]}
          trwale={Boolean(p.usunietaZAutomatow)}
          onClose={() => setUsuwanieOtwarte(false)}
          onUsunieto={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}

      {operacjeOtwarte && (
        <OperacjeModal
          cel="employee"
          id={p.id}
          islandId={groupId}
          canEdit={isAdmin}
          onClose={() => setOperacjeOtwarte(false)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}

      {/*
        ⚠️ OKNO HISTORII OBOK OKNA ZMIANY NUMERU, nie w nim: to dwa różne pytania. Zmiana pyta
        „na jaki numer", historia odpowiada na „pod jakim numerem szukać starszych wydań".
      */}
      {historiaKarty && (
        <HistoriaKarty
          employeeId={p.id}
          groupId={groupId}
          pracownik={p.name}
          onClose={() => setHistoriaKarty(false)}
        />
      )}

      {numerOtwarty && (
        <ZmianaNumeruKarty
          employeeId={p.id}
          islandId={groupId}
          obecnyNumer={p.spsUserNumber}
          nazwisko={p.name}
          onClose={() => setNumerOtwarty(false)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employees'] })}
        />
      )}
    </div>
  );
}

/** Pas ostrzegawczy nad treścią — jeden kształt dla trzech powodów, dla których ktoś nie pobierze. */
function Pas({
  ton,
  ikona: Ikona,
  children,
}: {
  ton: 'destructive' | 'warning' | 'neutral';
  ikona: typeof Users;
  children: React.ReactNode;
}) {
  const klasy =
    ton === 'destructive'
      ? 'border-[var(--destructive)]/40 bg-[var(--destructive)]/10'
      : ton === 'warning'
        ? 'border-[var(--warning)]/40 bg-[var(--warning)]/10'
        : 'border-border bg-muted/40';
  return (
    <p className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${klasy}`}>
      <Ikona className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/**
 * Inicjały — pierwsze litery dwóch pierwszych członów nazwiska.
 * Ta sama zasada co na liście pracowników; dane z SPS mieszają wielkość liter.
 */
function inicjaly(nazwa: string): string {
  const czlony = nazwa.trim().split(/\s+/).filter(Boolean);
  if (czlony.length === 0) return '?';
  return (czlony[0]![0]! + (czlony[1]?.[0] ?? '')).toUpperCase();
}
